type LoadState = {
  isError: boolean;
  error: unknown;
  retry: () => unknown;
};

type LoadFailure = {
  title: string;
  summary: string;
  error: unknown;
  retry: () => unknown;
};

type BlockingPageInput = {
  sceneNotFound: boolean;
  wrongBrowser: boolean;
  loadFailure: LoadFailure | {title: string} | null;
};

type RenderReadyInput = {
  sceneId?: string;
  isSceneLoaded: boolean;
  hasRouteDefaultScene: boolean;
  isInitialSceneLoaded: boolean;
  loadFailure: {title: string; summary: string} | null;
};

type LoadStates = {
  scene?: LoadState;
  /** The saved scene names template designs that this build does not have. */
  sceneTemplates?: LoadState;
  defaultScenes?: LoadState;
  initialScene?: LoadState;
  backgrounds?: LoadState;
};

function errorStatus(error: unknown): number | null {
  if (!error || typeof error !== "object" || !("status" in error)) return null;
  return typeof error.status === "number" ? error.status : null;
}

export function selectLoadFailure(states: LoadStates): LoadFailure | null {
  if (states.scene?.isError && errorStatus(states.scene.error) !== 404) {
    return {
      title: "Couldn't load scene",
      summary: "The saved scene couldn't be loaded. Retry the request before editing or exporting.",
      error: states.scene.error,
      retry: states.scene.retry,
    };
  }

  if (states.sceneTemplates?.isError) {
    return {
      title: "Couldn't open the scene's templates",
      summary:
        "The scene uses template designs that this version of the editor does not have. Reload the page to get the newest version.",
      error: states.sceneTemplates.error,
      retry: states.sceneTemplates.retry,
    };
  }

  if (states.defaultScenes?.isError) {
    return {
      title: "Couldn't load defaults",
      summary:
        "The editor defaults couldn't be loaded. Retry to avoid opening an incomplete layout.",
      error: states.defaultScenes.error,
      retry: states.defaultScenes.retry,
    };
  }

  if (states.initialScene?.isError) {
    return {
      title: "Couldn't load defaults",
      summary: "The initial scene couldn't be loaded. Retry to avoid opening an incomplete layout.",
      error: states.initialScene.error,
      retry: states.initialScene.retry,
    };
  }

  if (states.backgrounds?.isError) {
    return {
      title: "Couldn't load backgrounds",
      summary: "Backgrounds or their saved transforms couldn't be loaded. Retry before editing.",
      error: states.backgrounds.error,
      retry: states.backgrounds.retry,
    };
  }

  return null;
}

export function selectBlockingPage({
  sceneNotFound,
  wrongBrowser,
  loadFailure,
}: BlockingPageInput): "scene-not-found" | "wrong-browser" | "load-error" | null {
  if (sceneNotFound) return "scene-not-found";
  if (wrongBrowser) return "wrong-browser";
  if (loadFailure) return "load-error";
  return null;
}

export function getRenderReadyState({
  sceneId,
  isSceneLoaded,
  hasRouteDefaultScene,
  isInitialSceneLoaded,
  loadFailure,
}: RenderReadyInput) {
  if (loadFailure) {
    return {
      ready: false,
      error: {title: loadFailure.title, summary: loadFailure.summary},
    };
  }
  const savedSceneReady = !sceneId || isSceneLoaded;
  const routeDefaultReady = Boolean(sceneId) || !hasRouteDefaultScene || isInitialSceneLoaded;
  return {ready: savedSceneReady && routeDefaultReady, error: null};
}
